import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { cloneDeep, set as lset } from "lodash";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppHeader } from "@/components/AppHeader";
import { KpiStrip } from "@/components/KpiStrip";
import { MissingBar } from "@/components/MissingBar";
import { Headlines, StatusCards } from "@/components/StatusCards";
import { ClientFamilyTab } from "@/components/tabs/ClientFamilyTab";
import { FinancialTab } from "@/components/tabs/FinancialTab";
import { InvestRetireTab } from "@/components/tabs/InvestRetireTab";
import { InsuranceTab } from "@/components/tabs/InsuranceTab";
import { isSubmitted, portalProgress, ProgressSteps, tabDone } from "@/components/PortalProgress";
import { ReportReady, ThankYou } from "@/components/ThankYou";
import { errMsg, portalApi } from "@/lib/api";
import { computeSynopsis } from "@/lib/calc";
import { migrate } from "@/lib/migrate";
import { requiredState } from "@/lib/required";

const TABS = [["client", "1 · My Personal & Family Information", "1 · Me & Family"], ["financial", "2 · Income, Expenses & Assets", "2 · Money"], ["investment", "3 · Investments & Retirement", "3 · Retirement"], ["insurance", "4 · Insurance Needs", "4 · Insurance"], ["summary", "5 · Financial Summary & Submit", "5 · Submit"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "");

const StepNav = ({ tab, go }) => {
  const i = TABS.findIndex(([k]) => k === tab);
  const prev = TABS[i - 1];
  const next = TABS[i + 1];
  return (
    <div className="step-nav" data-testid="portal-step-nav">
      {prev ? <Button variant="outline" onClick={() => go(prev[0])} data-testid="portal-prev-button" className="gap-1.5"><ChevronLeft className="h-4 w-4" /> Back</Button> : <span />}
      {next && <Button onClick={() => go(next[0])} data-testid="portal-next-button" className="gap-1.5">Next: {next[2].split(" · ")[1]} <ChevronRight className="h-4 w-4" /></Button>}
    </div>
  );
};

const StatusBanner = ({ a }) => {
  if (a.status === "in_progress" && a.resubmit_needed) return <div className="portal-banner is-amber" data-testid="portal-status">You changed your assessment after submitting it (v{a.last_submission?.version}). Your advisor still has the earlier version. Please review and resubmit.</div>;
  if (a.status === "submitted" || a.status === "reviewed") return <div className="portal-banner is-green" data-testid="portal-status">Submitted to your advisor on {fmt(a.last_submission?.submitted_at)} (version {a.last_submission?.version}). You can still update it and resubmit.</div>;
  return <div className="portal-banner" data-testid="portal-status">Draft. Your answers save automatically, so you can stop and continue later. Submit to your advisor when you're ready.</div>;
};

const SubmitPanel = ({ s, cur, privacy, missing, onSubmit, busy }) => {
  const [agree, setAgree] = useState(false);
  return (
    <div className="space-y-4">
      <Headlines s={s} cur={cur} testId="portal-headlines" />
      <StatusCards s={s} cur={cur} testId="portal-status-cards" />
      <section className="panel" data-testid="submit-panel">
        <div className="panel-head"><h3>Review & submit to your advisor</h3></div>
        {missing > 0 && <p className="text-sm text-amber-700 mb-2" data-testid="submit-missing-warning">{missing} required field(s) are still empty (see the list at the top). You can still submit, but the results will be less accurate.</p>}
        <div className="privacy-box" data-testid="privacy-notice">{privacy.text}</div>
        <label className="flex items-start gap-2 mt-3 text-sm text-slate-700 cursor-pointer">
          <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="consent-checkbox" className="mt-0.5" />
          I have read the privacy notice and consent to sharing this information with my advisor, Shalindra K C, for my financial needs analysis.
        </label>
        <Button className="mt-3 gap-1.5" disabled={!agree || busy} onClick={() => onSubmit(() => setAgree(false))} data-testid="submit-to-advisor-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit to Advisor
        </Button>
        <p className="text-[11px] text-slate-500 mt-2">Results are estimates based on the information and assumptions provided. They are not guarantees of future results.</p>
      </section>
    </div>
  );
};

export default function PortalPage() {
  const [a, setA] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("client");
  const [advisor, setAdvisor] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const edits = useRef(0);
  const load = () => portalApi.me().then((r) => { setA(r.assessment); setPrivacy(r.privacy); setAdvisor(r.advisor); setData(migrate(r.assessment.data)); setDirty(false); });
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, []);
  const s = useMemo(() => (data ? computeSynopsis(data) : null), [data]);
  const req = useMemo(() => (s ? requiredState(s, true) : null), [s]);
  const visited = data?.portal?.visited || [];
  const submitted = a ? isSubmitted(a) : false;
  const pct = req ? portalProgress(req, visited, TABS, submitted) : 0;
  const set = (path, v) => { edits.current += 1; setData((d) => lset(cloneDeep(d), path, v)); setDirty(true); };
  useEffect(() => { if (data && !visited.includes(tab)) set("portal.visited", [...visited, tab]); }, [data, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const version = edits.current;
    setSaving(true);
    try {
      const r = await portalApi.save(data, pct);
      if (edits.current === version) setDirty(false);
      setA((x) => ({ ...x, status: r.status, resubmit_needed: r.resubmit_needed }));
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  useEffect(() => {
    if (!dirty || saving) return undefined;
    const t = setTimeout(save, 1200);
    return () => clearTimeout(t);
  }, [data, dirty, saving]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const submit = async (reset) => {
    setSubmitting(true);
    try {
      if (dirty) await save();
      const r = await portalApi.submit(privacy.version);
      toast.success(`Submitted to your advisor (version ${r.version}). A confirmation email is on its way.`);
      reset(); await load(); setReviewing(false); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(errMsg(e)); }
    setSubmitting(false);
  };
  const tabsRef = useRef(null);
  const go = (k) => { setTab(k); setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const jump = (m) => { setTab(m.tab); setTimeout(() => document.querySelector(`[data-testid="${m.target}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80); };

  if (!s) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0072C6]" /></div>;
  const cur = data.profile.currency;
  const props = { d: data, s, set, cur, req, clientMode: true, products: [] };
  return (
    <div className="min-h-screen portal-page">
      <AppHeader>
        <span className="text-xs whitespace-nowrap" data-testid="portal-save-state">{dirty || saving ? <span className="text-amber-600 font-semibold inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /><span className="hidden sm:inline">Saving draft…</span></span> : <span className="text-emerald-600 font-medium inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /><span className="hidden sm:inline">Draft saved</span><span className="sm:hidden">Saved</span></span>}</span>
        <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="save-draft-button"><span className="hidden sm:inline">Save draft</span><span className="sm:hidden">Save</span></Button>
      </AppHeader>
      <main className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-5" data-testid="client-portal">
        {submitted && !reviewing ? (
          <ThankYou name={data.profile.name || a.name} advisor={advisor} submittedAt={fmt(a.last_submission?.submitted_at)} version={a.last_submission?.version} onReview={() => setReviewing(true)} sharedAt={a.shared_report_at ? fmt(a.shared_report_at) : null} />
        ) : (<>
        {a.shared_report_at && <ReportReady sharedAt={fmt(a.shared_report_at)} />}
        <div className="sticky-top">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <h1 className="font-heading text-lg sm:text-xl font-extrabold text-[#0b2545] leading-tight" data-testid="portal-client-name">My financial assessment · {data.profile.name || a.name}</h1>
          </div>
          <StatusBanner a={a} />
          <KpiStrip s={s} cur={cur} />
          <MissingBar list={req.list} onJump={jump} />
        </div>
        <ProgressSteps tabs={TABS} tab={tab} pct={pct} done={(k) => tabDone(k, req, visited, submitted)} onGo={go} />
        <Tabs value={tab} onValueChange={go} className="mt-3">
          <div ref={tabsRef} className="portal-tabs-anchor" />
          <TabsList className="tab-list portal-tabs">
            {TABS.map(([k, label, short]) => {
              const count = req.list.filter((m) => m.tab === k).length;
              const ok = tabDone(k, req, visited, submitted);
              return <TabsTrigger key={k} value={k} data-testid={`portal-tab-${k}`} className="tab-trigger">{ok && <CheckCircle2 className="h-3.5 w-3.5 mr-1 tab-check" data-testid={`portal-tab-done-${k}`} />}<span className="tab-long">{label}</span><span className="tab-short">{short}</span>{count > 0 && <span className="tab-badge">{count}</span>}</TabsTrigger>;
            })}
          </TabsList>
          <TabsContent value="client"><ClientFamilyTab {...props} /><StepNav tab={tab} go={go} /></TabsContent>
          <TabsContent value="financial"><FinancialTab {...props} /><StepNav tab={tab} go={go} /></TabsContent>
          <TabsContent value="investment"><InvestRetireTab {...props} /><StepNav tab={tab} go={go} /></TabsContent>
          <TabsContent value="insurance"><InsuranceTab {...props} /><StepNav tab={tab} go={go} /></TabsContent>
          <TabsContent value="summary"><SubmitPanel s={s} cur={cur} privacy={privacy} missing={req.list.length} onSubmit={submit} busy={submitting} /><StepNav tab={tab} go={go} /></TabsContent>
        </Tabs>
        </>)}
      </main>
    </div>
  );
}
